import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { awardedRegions, awardedPropertyTypes, awardedGrades } from "@/mocks/awardedItems";
import { useAwardedItems } from "@/hooks/useAwardedItems";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { motion } from "motion/react";

const statsContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1, delayChildren: 0.2 } },
};

const statsItem = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1] as const } },
};

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1] as const } },
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08, delayChildren: 0.05 } },
};

export default function AwardedInsightsPage() {
  const { items: awardedItems, loading } = useAwardedItems();
  const [searchQuery, setSearchQuery] = useState("");
  const [region, setRegion] = useState("전체");
  const [propertyType, setPropertyType] = useState("전체");
  const [grade, setGrade] = useState("전체");
  const [sortBy, setSortBy] = useState("auctionDate");
  const [showMobileFilter, setShowMobileFilter] = useState(false);

  const filteredItems = useMemo(() => {
    let items = [...awardedItems];

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      items = items.filter(
        (item) =>
          item.caseNumber.toLowerCase().includes(q) ||
          item.location.toLowerCase().includes(q) ||
          item.court.toLowerCase().includes(q) ||
          item.propertyType.toLowerCase().includes(q)
      );
    }

    if (region !== "전체") {
      items = items.filter((item) => item.region === region);
    }

    if (propertyType !== "전체") {
      items = items.filter((item) => item.propertyType === propertyType);
    }

    if (grade !== "전체") {
      items = items.filter((item) => item.expertGrade === grade);
    }

    switch (sortBy) {
      case "profitHigh":
        items.sort((a, b) => (b.profitBreakdown?.profitRate ?? -1) - (a.profitBreakdown?.profitRate ?? -1));
        break;
      case "bidLow":
        items.sort((a, b) => a.actualWinningBid - b.actualWinningBid);
        break;
      case "bidHigh":
        items.sort((a, b) => b.actualWinningBid - a.actualWinningBid);
        break;
      case "competition":
        items.sort((a, b) => b.bidderCount - a.bidderCount);
        break;
      case "auctionDate":
      default:
        items.sort((a, b) => new Date(b.auctionDate).getTime() - new Date(a.auctionDate).getTime());
        break;
    }

    return items;
  }, [awardedItems, searchQuery, region, propertyType, grade, sortBy]);

  const totalProfitAvg = useMemo(() => {
    const withProfit = awardedItems.filter((i) => i.profitBreakdown);
    if (withProfit.length === 0) return null;
    return Math.round(withProfit.reduce((s, i) => s + i.profitBreakdown!.profitRate, 0) / withProfit.length);
  }, [awardedItems]);

  const totalBiddersAvg = useMemo(() => {
    if (awardedItems.length === 0) return 0;
    return Math.round(awardedItems.reduce((s, i) => s + i.bidderCount, 0) / awardedItems.length);
  }, [awardedItems]);

  const regionCount = useMemo(() => new Set(awardedItems.map((i) => i.region).filter(Boolean)).size, [awardedItems]);

  return (
    <div className="min-h-screen bg-background-50 font-['Noto_Sans_KR']">
      <Navbar />
      <main className="pt-24 pb-16 px-4 md:px-6">
        <div className="max-w-[1280px] mx-auto">
          {/* Page Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] as const }}
            className="mb-8"
          >
            <div className="flex items-center gap-2 mb-2">
              <i className="ri-history-line text-[#C4956A]"></i>
              <span className="text-xs font-medium text-[#C4956A]">사후 분석 데이터</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
              낙찰물건 인사이트
            </h1>
            <p className="text-sm text-foreground-600 max-w-[600px]">
              실제 낙찰된 경매 물건의 사후 분석 데이터입니다. 낙찰가, 경쟁률, 수익률을 꼼꼼히 분석하고
              전문가의 인사이트와 교훈까지 — 마치 옆에서 같이 리뷰해주는 느낌으로 정리했습니다.
            </p>
          </motion.div>

          {/* Stats Bar */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={statsContainer}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10"
          >
            <motion.div variants={statsItem} className="bg-white rounded-xl border border-[#E8E5E0] p-5 text-center">
              <p className="text-2xl md:text-3xl font-bold text-[#C4956A]">{awardedItems.length}<span className="text-base font-normal text-[#8C8780]">건</span></p>
              <p className="text-xs text-[#8C8780] mt-1">분석 완료된 낙찰 사례</p>
            </motion.div>
            <motion.div variants={statsItem} className="bg-white rounded-xl border border-[#E8E5E0] p-5 text-center">
              <p className="text-2xl md:text-3xl font-bold text-emerald-600">
                {totalProfitAvg != null ? totalProfitAvg : "-"}
                <span className="text-base font-normal text-[#8C8780]">%</span>
              </p>
              <p className="text-xs text-[#8C8780] mt-1">평균 예상 수익률</p>
            </motion.div>
            <motion.div variants={statsItem} className="bg-white rounded-xl border border-[#E8E5E0] p-5 text-center">
              <p className="text-2xl md:text-3xl font-bold text-foreground-950">{totalBiddersAvg}<span className="text-base font-normal text-[#8C8780]">명</span></p>
              <p className="text-xs text-[#8C8780] mt-1">건당 평균 응찰자</p>
            </motion.div>
            <motion.div variants={statsItem} className="bg-white rounded-xl border border-[#E8E5E0] p-5 text-center">
              <p className="text-2xl md:text-3xl font-bold text-foreground-950">{regionCount}<span className="text-base font-normal text-[#8C8780]">개</span></p>
              <p className="text-xs text-[#8C8780] mt-1">커버 지역</p>
            </motion.div>
          </motion.div>

          {/* Search & Sort + Filter Chips */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.4, ease: "easeOut" as const }}
            className="flex flex-col sm:flex-row gap-3 mb-4"
          >
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#8C8780]"></i>
              <input
                type="text"
                placeholder="사건번호, 소재지, 법원으로 검색"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 text-sm border border-[#E8E5E0] rounded-lg bg-white focus:outline-none focus:border-[#C4956A] transition-colors"
              />
            </div>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2.5 text-sm border border-[#E8E5E0] rounded-lg bg-white text-foreground-700 focus:outline-none focus:border-[#C4956A] cursor-pointer"
            >
              <option value="auctionDate">낙찰일 최신순</option>
              <option value="profitHigh">수익률 높은순</option>
              <option value="bidLow">낙찰가 낮은순</option>
              <option value="bidHigh">낙찰가 높은순</option>
              <option value="competition">경쟁률 높은순</option>
            </select>
            <button
              onClick={() => setShowMobileFilter(!showMobileFilter)}
              className="lg:hidden flex items-center gap-2 px-4 py-2.5 text-sm border border-[#E8E5E0] rounded-lg bg-white text-foreground-700 hover:border-[#C4956A] transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-filter-3-line"></i>
              필터
            </button>
          </motion.div>

          {/* Filter Chips */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45, duration: 0.4, ease: "easeOut" as const }}
            className={`flex flex-wrap items-center gap-2 mb-6 ${showMobileFilter ? "flex" : "hidden lg:flex"}`}
          >
            <div className="flex items-center gap-1 bg-[#FAFAF8] rounded-full px-1 py-1 border border-[#E8E5E0]">
              <span className="text-xs text-[#8C8780] pl-2 pr-1 whitespace-nowrap">지역</span>
              {awardedRegions.map((r) => (
                <button
                  key={r}
                  onClick={() => setRegion(r)}
                  className={`px-3 py-1.5 text-xs rounded-full transition-all whitespace-nowrap cursor-pointer ${region === r ? "bg-[#C4956A] text-white font-medium" : "text-foreground-700 hover:bg-[#E8E5E0]"}`}
                >
                  {r}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1 bg-[#FAFAF8] rounded-full px-1 py-1 border border-[#E8E5E0]">
              <span className="text-xs text-[#8C8780] pl-2 pr-1 whitespace-nowrap">물건</span>
              {awardedPropertyTypes.map((t) => (
                <button
                  key={t}
                  onClick={() => setPropertyType(t)}
                  className={`px-3 py-1.5 text-xs rounded-full transition-all whitespace-nowrap cursor-pointer ${propertyType === t ? "bg-[#C4956A] text-white font-medium" : "text-foreground-700 hover:bg-[#E8E5E0]"}`}
                >
                  {t}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1 bg-[#FAFAF8] rounded-full px-1 py-1 border border-[#E8E5E0]">
              <span className="text-xs text-[#8C8780] pl-2 pr-1 whitespace-nowrap">평가</span>
              {awardedGrades.map((g) => (
                <button
                  key={g}
                  onClick={() => setGrade(g)}
                  className={`px-3 py-1.5 text-xs rounded-full transition-all whitespace-nowrap cursor-pointer ${grade === g ? "bg-[#C4956A] text-white font-medium" : "text-foreground-700 hover:bg-[#E8E5E0]"}`}
                >
                  {g}
                </button>
              ))}
            </div>
          </motion.div>

          {/* Result count */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.4 }}
            className="text-xs text-[#8C8780] mb-5"
          >
            총 <strong className="text-foreground-950">{filteredItems.length}</strong>건의 낙찰 분석 사례
          </motion.p>

          {/* Loading */}
          {loading && (
            <div className="py-20 text-center">
              <div className="w-8 h-8 mx-auto border-2 border-[#E8E5E0] border-t-[#C4956A] rounded-full animate-spin" />
            </div>
          )}

          {/* Grid */}
          {!loading && (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
          >
            {filteredItems.map((item) => (
              <motion.div
                key={item.id}
                variants={cardVariants}
                whileHover={{ y: -6 }}
                transition={{ type: "spring", stiffness: 300, damping: 15 }}
              >
                <Link
                  to={`/awarded-insights/${item.id}`}
                  className="group bg-white rounded-xl border border-[#E8E5E0] overflow-hidden hover:border-[#C4956A]/30 transition-all block"
                >
                  {/* Image */}
                  <div className="relative h-52 overflow-hidden">
                    <img loading="lazy" decoding="async"
                      src={item.imageUrl}
                      alt={`${item.propertyType} - ${item.location}`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 left-3">
                      <span className={`px-2.5 py-1 text-xs font-semibold rounded-full ${item.expertGrade === "적극추천" ? "bg-emerald-500 text-white" : item.expertGrade === "추천" ? "bg-amber-500 text-white" : "bg-gray-400 text-white"}`}>
                        {item.expertGrade}
                      </span>
                    </div>
                    <div className="absolute top-3 right-3">
                      <span className="px-2.5 py-1 text-xs font-medium bg-white/90 rounded-full text-[#1C1917]">
                        응찰 {item.bidderCount}명
                      </span>
                    </div>
                    <div className="absolute bottom-3 left-3 right-3">
                      <span className="px-3 py-1.5 text-xs font-bold bg-[#1C1917]/80 backdrop-blur-sm text-white rounded-full">
                        낙찰가 {item.actualWinningBid.toLocaleString()}원
                      </span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs font-medium text-[#C4956A] bg-[#C4956A]/5 px-2 py-0.5 rounded">
                        {item.propertyType}
                      </span>
                      <span className="text-xs text-[#8C8780]">{item.court}</span>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-950 mb-2 line-clamp-1">
                      {item.location}
                    </h3>
                    <p className="text-xs text-foreground-600 leading-relaxed line-clamp-2 mb-3">
                      {item.expertSummary}
                    </p>

                    <div className="space-y-1.5 mb-3">
                      <div className="flex justify-between text-xs">
                        <span className="text-[#8C8780]">감정가</span>
                        <span className="text-foreground-700 font-medium">{item.appraisedValue.toLocaleString()}원</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-[#8C8780]">최저가</span>
                        <span className="text-[#C4956A] font-bold">{item.minimumPrice.toLocaleString()}원</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-[#8C8780]">낙찰가</span>
                        <span className="text-[#1C1917] font-bold">{item.actualWinningBid.toLocaleString()}원</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-[#8C8780]">낙찰일</span>
                        <span className="text-foreground-700 font-medium">{item.auctionDate}</span>
                      </div>
                    </div>

                    <div className="pt-3 border-t border-[#F0EDE8] flex items-center justify-between">
                      <div className="flex items-center gap-1 text-xs text-[#8C8780]">
                        <i className="ri-group-line"></i>
                        <span>경쟁 {item.bidCompetitionRate}</span>
                      </div>
                      {item.profitBreakdown && (
                        <span className="text-xs font-bold text-emerald-600">
                          예상수익률 {item.profitBreakdown.profitRate}%
                        </span>
                      )}
                    </div>

                    {/* Key lessons snippet */}
                    {item.keyLessons && item.keyLessons.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-[#F0EDE8]">
                        <div className="flex items-start gap-1.5">
                          <i className="ri-lightbulb-line text-[#C4956A] text-xs mt-0.5 flex-shrink-0"></i>
                          <p className="text-xs text-foreground-600 leading-relaxed line-clamp-1">
                            <span className="font-medium text-foreground-950">핵심 교훈:</span> {item.keyLessons[0]}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>
          )}

          {/* Empty State */}
          {!loading && filteredItems.length === 0 && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4 }}
              className="text-center py-20"
            >
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-[#C4956A]/10 flex items-center justify-center">
                <i className="ri-search-line text-2xl text-[#C4956A]"></i>
              </div>
              <p className="text-foreground-700 font-medium mb-1">검색 결과가 없습니다</p>
              <p className="text-sm text-[#8C8780]">다른 검색어나 필터 조건으로 다시 시도해보세요</p>
            </motion.div>
          )}

          {/* Bottom CTA - 교육 연계 */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ delay: 0.3, duration: 0.6, ease: [0.25, 0.1, 0.25, 1] as const }}
            className="mt-16 bg-white rounded-2xl border border-[#E8E5E0] p-8 md:p-10"
          >
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="flex-1 text-center md:text-left">
                <div className="flex items-center gap-2 mb-2 justify-center md:justify-start">
                  <i className="ri-graduation-cap-line text-[#C4956A]"></i>
                  <span className="text-xs font-medium text-[#C4956A]">교육 연계</span>
                </div>
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-3">
                  이런 분석 능력, 직접 키워보세요
                </h2>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4 max-w-[500px]">
                  낙찰물건 인사이트는 강스터디 교육과정에서 실제 사례로 활용됩니다.
                  전문가의 분석 방식을 배우고, 직접 입찰계산기와 시뮬레이터로 실습해보세요.
                  이론으로 배우고, 데이터로 검증하는 — 그게 강스터디 방식입니다.
                </p>
                <div className="flex flex-wrap gap-3 justify-center md:justify-start">
                  <Link
                    to="/courses"
                    className="px-6 py-2.5 bg-[#C4956A] text-white text-sm font-semibold rounded-full hover:bg-[#B08050] transition-all whitespace-nowrap inline-flex items-center gap-2"
                  >
                    <i className="ri-book-open-line"></i>
                    교육과정 보기
                  </Link>
                  <Link
                    to="/bid-calculator"
                    className="px-6 py-2.5 border-2 border-[#E8E5E0] text-foreground-700 text-sm font-medium rounded-full hover:border-[#C4956A] hover:text-[#C4956A] transition-all whitespace-nowrap inline-flex items-center gap-2"
                  >
                    <i className="ri-calculator-line"></i>
                    입찰계산기
                  </Link>
                  <Link
                    to="/investment-simulator"
                    className="px-6 py-2.5 border-2 border-[#E8E5E0] text-foreground-700 text-sm font-medium rounded-full hover:border-[#C4956A] hover:text-[#C4956A] transition-all whitespace-nowrap inline-flex items-center gap-2"
                  >
                    <i className="ri-robot-2-line"></i>
                    투자 시뮬레이터
                  </Link>
                </div>
              </div>
              <div className="w-full md:w-[280px] flex-shrink-0">
                <div className="bg-[#FAFAF8] rounded-xl p-5 border border-[#E8E5E0]">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-[#C4956A]/10 flex items-center justify-center">
                      <i className="ri-sparkling-line text-[#C4956A] text-sm"></i>
                    </div>
                    <span className="text-sm font-bold text-foreground-950">이번 주 신규 인사이트</span>
                  </div>
                  <p className="text-xs text-foreground-600 mb-3">
                    매주 새로운 낙찰 사례가 추가됩니다. 놓치지 마세요!
                  </p>
                  <Link
                    to="/auctions"
                    className="flex items-center gap-2 text-xs font-medium text-[#C4956A] hover:text-[#B08050] transition-colors"
                  >
                    현재 진행 중인 경매 물건도 확인하기
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </main>
      <Footer />
    </div>
  );
}